import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefLabel } from '@/components/refLabel';
import { Icon } from '@iconify/react';
import { ProgressBar } from '@heroui/react';
import { EmptyState, NumberValue, Widget } from '@heroui-pro/react';
import type { TopProducts } from '@/api/models';
import {
    TOP_PRODUCTS_MEDAL_COLOR_CLASS,
    TOP_PRODUCTS_MEDAL_ICON,
    TOP_PRODUCTS_PERIOD_LABEL_KEY,
    getLeaderRevenue,
    getShareOfTop,
    isMedalRank,
} from './topProductsMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatGel } from '@/utils/money';

type TopProductsViewProps = {
    data: TopProducts;
    'data-testid': string;
    id: string;
};

export const TopProductsView: React.FC<TopProductsViewProps> = ({
    data,
    id,
}) => {
    const { t } = useTranslation('pages');

    const isEmpty = data.items.length === 0;

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.topProducts.title'),
            // With no items the "Top {{count}}" phrasing reads as a bug
            // ("Top 0"), so the subtitle falls back to the bare period label.
            subtitle: isEmpty
                ? t(TOP_PRODUCTS_PERIOD_LABEL_KEY[data.period])
                : t('sales.widgets.topProducts.subtitle', {
                      count: data.items.length,
                      period: t(TOP_PRODUCTS_PERIOD_LABEL_KEY[data.period]),
                  }),
            unitsSuffix: t('sales.widgets.topProducts.unitsSuffix'),
            marginSuffix: t('sales.widgets.topProducts.marginSuffix'),
            shareLabel: t('sales.widgets.topProducts.shareOfTopAria'),
            emptyTitle: t('sales.widgets.topProducts.empty.title'),
            emptyDescription: t('sales.widgets.topProducts.empty.description'),
        }),
        [t, isEmpty, data.items.length, data.period],
    );

    const leaderRevenue = React.useMemo(
        () => getLeaderRevenue(data.items),
        [data.items],
    );

    return (
        <section
            aria-labelledby="top-products-widget-title"
            {...getNodeTestIdProp('top-products-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('top-products-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('top-products-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('top-products-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp('top-products-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:cup-star-bold-duotone"
                                {...getNodeTestIdProp('top-products-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp('top-products-widget-subtitle')}
                    >
                        {copy.subtitle}
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content
                    className={
                        isEmpty
                            ? 'flex min-h-[220px] items-center justify-center'
                            : undefined
                    }
                >
                    {isEmpty ? (
                        <EmptyState
                            {...getNodeTestIdProp('top-products-empty')}
                        >
                            <EmptyState.Header>
                                <EmptyState.Media variant="icon">
                                    <Icon
                                        aria-hidden
                                        className="size-6"
                                        icon="solar:cup-star-bold-duotone"
                                        {...getNodeTestIdProp(
                                            'top-products-empty-icon',
                                        )}
                                    />
                                </EmptyState.Media>
                                <EmptyState.Title
                                    {...getNodeTestIdProp(
                                        'top-products-empty-title',
                                    )}
                                >
                                    {copy.emptyTitle}
                                </EmptyState.Title>
                                <EmptyState.Description
                                    {...getNodeTestIdProp(
                                        'top-products-empty-description',
                                    )}
                                >
                                    {copy.emptyDescription}
                                </EmptyState.Description>
                            </EmptyState.Header>
                        </EmptyState>
                    ) : (
                        <ul
                            aria-label={copy.title}
                            className="-mr-2 flex max-h-88 flex-col gap-3 overflow-y-auto rounded-md pr-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                            tabIndex={0}
                            {...getNodeTestIdProp('top-products-list')}
                        >
                            {data.items.map((item, index) => {
                                const rank = index + 1;
                                const itemId = `${id}-item-${item.productId}`;
                                const share = getShareOfTop(
                                    item.revenue,
                                    leaderRevenue,
                                );
                                const isLast = index === data.items.length - 1;
                                return (
                                    <li
                                        key={item.productId}
                                        className="flex flex-col gap-1.5"
                                        {...getNodeTestIdProp(
                                            `top-products-item-${itemId}`,
                                        )}
                                    >
                                        <div
                                            className="flex items-center gap-3"
                                            {...getNodeTestIdProp(
                                                `top-products-item-root-${itemId}`,
                                            )}
                                        >
                                            <div
                                                aria-hidden
                                                className="flex w-7 shrink-0 items-center justify-center"
                                                {...getNodeTestIdProp(
                                                    `top-products-item-rank-${itemId}`,
                                                )}
                                            >
                                                {isMedalRank(rank) ? (
                                                    <Icon
                                                        className={`size-4 ${TOP_PRODUCTS_MEDAL_COLOR_CLASS[rank]}`}
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-rank-icon-${itemId}`,
                                                        )}
                                                        icon={
                                                            TOP_PRODUCTS_MEDAL_ICON[
                                                                rank
                                                            ]
                                                        }
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-rank-icon-${itemId}`,
                                                        )}
                                                    />
                                                ) : (
                                                    <span
                                                        className="text-xs font-semibold text-muted"
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-rank-number-${itemId}`,
                                                        )}
                                                        id={`${itemId}-rank-number`}
                                                    >
                                                        {rank}
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className="flex min-w-0 flex-1 flex-col gap-1.5"
                                                {...getNodeTestIdProp(
                                                    `top-products-item-details-root-${itemId}`,
                                                )}
                                            >
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                    <div className="flex min-w-0 flex-col">
                                                        <span
                                                            className="truncate text-sm font-medium text-foreground"
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-name-${itemId}`,
                                                            )}
                                                        >
                                                            <RefLabel
                                                                archived={
                                                                    item.archived
                                                                }
                                                                deleted={
                                                                    item.deleted
                                                                }
                                                                id={
                                                                    item.productId
                                                                }
                                                                name={
                                                                    item.productName
                                                                }
                                                            />
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="flex shrink-0 items-end flex-row sm:flex-row-reverse sm:gap-2 sm:items-center"
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-meta-root-${itemId}`,
                                                        )}
                                                    >
                                                        {/* `formatGel` on the tetri twin, not Intl's currency style: the latter renders `GEL 455` while every other money figure in the app renders `₾ 455` (BOS-171). */}
                                                        <span
                                                            className="text-sm font-semibold text-success-soft-foreground tabular-nums"
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-revenue-${itemId}`,
                                                            )}
                                                        >
                                                            {formatGel(
                                                                item.revenueTetri,
                                                            )}
                                                        </span>
                                                        <span
                                                            className="text-[11px] text-muted ml-auto"
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-meta-${itemId}`,
                                                            )}
                                                        >
                                                            <NumberValue
                                                                maximumFractionDigits={
                                                                    0
                                                                }
                                                                style="decimal"
                                                                value={
                                                                    item.unitsSold
                                                                }
                                                                {...getNodeTestIdProp(
                                                                    `top-products-item-units-sold-${itemId}`,
                                                                )}
                                                            />
                                                            {` ${copy.unitsSuffix} · `}
                                                            <NumberValue
                                                                maximumFractionDigits={
                                                                    0
                                                                }
                                                                style="decimal"
                                                                value={
                                                                    item.marginPercent
                                                                }
                                                                {...getNodeTestIdProp(
                                                                    `top-products-item-margin-percent-${itemId}`,
                                                                )}
                                                            />
                                                            {`% ${copy.marginSuffix}`}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    className="flex items-center gap-2"
                                                    {...getNodeTestIdProp(
                                                        `top-products-item-progress-root-${itemId}`,
                                                    )}
                                                >
                                                    <ProgressBar
                                                        aria-label={
                                                            copy.shareLabel
                                                        }
                                                        size="lg"
                                                        value={share}
                                                        {...getNodeTestIdProp(
                                                            `top-products-item-progress-${itemId}`,
                                                        )}
                                                    >
                                                        <div
                                                            className="flex min-w-0 flex-col"
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-progress-label-root-${itemId}`,
                                                            )}
                                                        >
                                                            <span
                                                                className="text-[11px] text-muted"
                                                                {...getNodeTestIdProp(
                                                                    `top-products-item-category-${itemId}`,
                                                                )}
                                                            >
                                                                {
                                                                    item.categoryName
                                                                }
                                                            </span>
                                                        </div>
                                                        <ProgressBar.Output
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-progress-output-${itemId}`,
                                                            )}
                                                        />
                                                        <ProgressBar.Track
                                                            {...getNodeTestIdProp(
                                                                `top-products-item-progress-track-${itemId}`,
                                                            )}
                                                        >
                                                            <ProgressBar.Fill
                                                                {...getNodeTestIdProp(
                                                                    `top-products-item-progress-fill-${itemId}`,
                                                                )}
                                                            />
                                                        </ProgressBar.Track>
                                                    </ProgressBar>
                                                </div>
                                            </div>
                                        </div>
                                        {isLast ? null : (
                                            <div
                                                aria-hidden
                                                className="ml-10 border-b border-default-100"
                                                {...getNodeTestIdProp(
                                                    `top-products-item-separator-${itemId}`,
                                                )}
                                            />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Widget.Content>
            </Widget>
        </section>
    );
};
